/**
 * A crop enlarged (#678): the same band the card shows, at reading size, in a dialog over the page. The line's box is drawn about 44 CSS px tall
 * (never past the page's own pixels, never narrower than the dialog), and what is wider than the screen scrolls sideways, from the right edge for
 * handwriting that runs right to left. Native scrolling, so pinch zoom stays. It shows the learner their own handwriting: nothing is recorded.
 * Built like the note sheet: focus moves to Close, Tab stays inside, Escape or a press on the dimmed page closes it.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMessages } from '../../i18n/locale.js';
import { feedbackChrome } from './feedback-chrome.js';
import { pageImageUrl } from './feedback-page-image.js';
import { cropViewBox, displayScale, useMeasuredWidth, type Bbox } from './region-crop.js';
import { lineDir } from './untrusted-text.js';

export function CropZoom({ pageId, bbox, widthPx, heightPx, boxHeightPx, text, onClose }: {
  readonly pageId: string;
  readonly bbox: Bbox;
  readonly widthPx: number;
  readonly heightPx: number;
  readonly boxHeightPx: number;
  readonly text: string | undefined;
  readonly onClose: () => void;
}): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const dialog = useRef<HTMLDivElement | null>(null);
  const close = useRef<HTMLButtonElement | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  const available = useMeasuredWidth(scroller);
  const rtl = text !== undefined && lineDir(text) === 'rtl';
  // The same band the card draws (#637): a tighter one would cut ink.
  const { x, y, width, height } = cropViewBox(bbox, false, { widthPx, heightPx });
  const fit = available > 0 && width > 0 ? available / width : 0;
  const scale = Math.max(displayScale(bbox, boxHeightPx), fit);
  useEffect(() => {
    close.current?.focus();
  }, []);
  // Handwriting in Hebrew starts at the right edge.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (rtl && el !== null) el.scrollLeft = el.scrollWidth;
  }, [rtl, scale]);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || dialog.current === null) return;
    const focusable = [...dialog.current.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex]:not([tabindex="-1"])')];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  if (width <= 0 || height <= 0) return <></>;
  return createPortal(
    <>
      <button type="button" className="fb-scrim" aria-label={m.closeNote} tabIndex={-1} onClick={onClose} />
      <div ref={dialog} className="fb-sheet-modal" role="dialog" aria-modal="true" aria-label={m.enlargeCrop} onKeyDown={onKeyDown}>
        <span aria-hidden="true" className="fb-sheet-handle" />
        <div ref={scroller} className="fb-crop-zoom-scroll" dir="ltr" role="group" aria-label={m.enlargeCrop} tabIndex={0}>
          <svg
            className="fb-crop-zoom-picture"
            width={Math.round(width * scale)}
            height={Math.round(height * scale)}
            viewBox={`${String(x)} ${String(y)} ${String(width)} ${String(height)}`}
            aria-hidden="true"
            focusable="false"
          >
            <image href={pageImageUrl(pageId)} x={0} y={0} width={widthPx} height={heightPx} onError={onClose} />
          </svg>
        </div>
        <button ref={close} type="button" className="fb-sheet-close" onClick={onClose}>
          {m.closeNote}
        </button>
      </div>
    </>,
    document.body,
  );
}
